import { users } from "../../mocks/users.js";

// Simulate API delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const mockApi = {
  // Login endpoint
  login: async (username, password) => {
    await delay(800); // Simulate network delay

    const user = users.find(
      (u) => u.username === username && u.password === password
    );

    if (user) {
      // Create a mock JWT token (in real app, this would come from the server)
      const token = btoa(
        JSON.stringify({
          sub: user.id,
          name: user.name,
          role: user.role,
          exp: Date.now() + 3600000, // 1 hour expiration
        })
      );

      // Return user data without password and the token
      const { password: _, ...userWithoutPassword } = user;
      return { user: userWithoutPassword, token };
    }

    throw new Error("Invalid username or password");
  },

  // Get current user endpoint
  getCurrentUser: async (token) => {
    await delay(500);

    if (!token) {
      throw new Error("No token provided");
    }

    try {
      // Decode the token
      const decoded = JSON.parse(atob(token));

      // Check if token is expired
      if (decoded.exp < Date.now()) {
        throw new Error("Token expired");
      }

      // Find the user
      const user = users.find((u) => u.id === decoded.sub);

      if (!user) {
        throw new Error("User not found");
      }

      // Return user without password
      const { password: _, ...userWithoutPassword } = user;
      return userWithoutPassword;
    } catch (error) {
      throw new Error("Invalid token");
    }
  },

  // Register endpoint
  register: async (userData) => {
    await delay(1000);

    // Check if username already exists
    if (users.some((u) => u.username === userData.username)) {
      throw new Error("Username already exists");
    }

    // Create new user (in a real app, this would be saved to a database)
    const newUser = {
      id: `user-${Date.now()}`,
      ...userData,
      role: userData.role || "user",
    };

    // In a real implementation, we would add the user to the database
    // For mock purposes, we'll just return success
    const { password: _, ...userWithoutPassword } = newUser;

    return {
      user: userWithoutPassword,
      token: btoa(
        JSON.stringify({
          sub: newUser.id,
          name: newUser.name,
          role: newUser.role,
          exp: Date.now() + 3600000, // 1 hour expiration
        })
      ),
    };
  },

  // Forgot password endpoint
  forgotPassword: async (email) => {
    await delay(800);

    // Check if email exists
    const user = users.find((u) => u.email === email);
    if (!user) {
      throw new Error("Email not found");
    }

    // In a real implementation, we would send an email with a reset link
    // For mock purposes, we'll just return success
    return {
      success: true,
      message: "Password reset instructions sent to your email",
    };
  },

  // Reset password endpoint
  resetPassword: async (data) => {
    await delay(800);

    // In a real implementation, we would verify the token and update the password
    // For mock purposes, we'll just return success
    return {
      success: true,
      message: "Password has been reset successfully",
    };
  },

  // Change password endpoint
  changePassword: async (data) => {
    await delay(600);

    // In a real implementation, we would verify the current password and update to the new one
    // For mock purposes, we'll just return success
    return {
      success: true,
      message: "Password has been changed successfully",
    };
  },

  // Get users endpoint
  getUsers: async () => {
    await delay(700);

    // Return users without passwords
    return users.map(({ password: _, ...user }) => user);
  },

  // Update profile endpoint
  updateProfile: async (userData) => {
    await delay(800);

    // In a real implementation, we would update the user in the database
    // For mock purposes, we'll just return the updated user
    return {
      ...userData,
      updatedAt: new Date().toISOString(),
    };
  },
};
